import { c as _c } from "react/compiler-runtime";
import { useSearchPageInteraction, useSearchPageInteractionStore } from "@/hooks/search";
import { useOptimizedClicked } from "@/hooks/search/use-optimized-clicked";
import { usePropertyInteractionStore } from "@/stores/property-interaction-store";
import { MapItemSUP } from "@/types/search";
import { cn } from "@/utils/cn";
import React from "react";
import { SUPCard } from "../../sup/card";
export interface Project {
  projectId?: string;
  name?: string;
  sellerProjects?: string[];
  units?: string[];
  handout?: boolean;
}
interface SearchItemProps {
  data: MapItemSUP;
  rank: number;
  disableHighlight?: boolean;
  disableOnClick?: boolean;
  hChecked?: ((props: {
    id: string;
  }) => void) | undefined;
  checked?: boolean;
  className?: string;
}

// Priority loading for first 4 items (2x2 grid on desktop, or first 4 on mobile) to improve LCP
const LCP_PRIORITY_THRESHOLD = 4;
export const SearchItem = React.memo(function SearchItem(t0) {
  const $ = _c(46);
  const {
    data,
    rank,
    disableHighlight,
    disableOnClick,
    className
  } = t0;
  const {
    gps,
    coordinates: c,
    mapCoordinates
  } = data as MapItemSUP & {
    mapCoordinates?: [number, number] | string;
  };
  let t1;
  bb0: {
    if (mapCoordinates) {
      if (Array.isArray(mapCoordinates)) {
        t1 = mapCoordinates;
        break bb0;
      }
      if (typeof mapCoordinates === "string") {
        let lat;
        let lng;
        let t2;
        if ($[0] !== mapCoordinates) {
          const [lngStr, latStr] = mapCoordinates.split(",").map(_temp);
          lng = Number.parseFloat(lngStr ?? "");
          lat = Number.parseFloat(latStr ?? "");
          t2 = Number.isFinite(lng) && Number.isFinite(lat);
          $[0] = mapCoordinates;
          $[1] = lat;
          $[2] = lng;
          $[3] = t2;
        } else {
          lat = $[1];
          lng = $[2];
          t2 = $[3];
        }
        if (t2) {
          let t3;
          if ($[4] !== lat || $[5] !== lng) {
            t3 = [lng, lat];
            $[4] = lat;
            $[5] = lng;
            $[6] = t3;
          } else {
            t3 = $[6];
          }
          t1 = t3;
          break bb0;
        }
      }
    }
    if (gps) {
      let t2;
      if ($[7] !== gps.lat || $[8] !== gps.lng) {
        t2 = [gps.lng, gps.lat];
        $[7] = gps.lat;
        $[8] = gps.lng;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      t1 = t2;
      break bb0;
    }
    t1 = c;
  }
  const coordinates = t1;
  let t2;
  bb1: {
    if (!coordinates) {
      t2 = "";
      break bb1;
    }
    if (Array.isArray(coordinates)) {
      let t3;
      if ($[10] !== coordinates) {
        t3 = coordinates.toString();
        $[10] = coordinates;
        $[11] = t3;
      } else {
        t3 = $[11];
      }
      t2 = t3;
      break bb1;
    }
    if (typeof coordinates === "string") {
      t2 = coordinates;
      break bb1;
    }
    t2 = "";
  }
  const coordinatesString = t2;
  const elementRef = React.useRef(null);
  const isHoveredRef = React.useRef(false);
  const isGloballyHoveredRef = React.useRef(false);
  const {
    isClicked,
    handleToggleClick
  } = useOptimizedClicked(coordinatesString);
  const hoverKey = `${data._id}-${coordinatesString}`;
  const interactionStore = useSearchPageInteractionStore();
  const setHoveredMarkerId = useSearchPageInteraction(_temp2);
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = isHovered => {
      if (elementRef.current) {
        const element = elementRef.current;
        if (isHovered) {
          element.classList.add("shadow-xl", "scale-[1.02]");
        } else {
          element.classList.remove("shadow-xl", "scale-[1.02]");
        }
      }
    };
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const applyHoverEffects = t3;
  let t4;
  if ($[13] !== hoverKey || $[14] !== setHoveredMarkerId) {
    t4 = () => {
      isHoveredRef.current = true;
      applyHoverEffects(true);
      setHoveredMarkerId(hoverKey);
    };
    $[13] = hoverKey;
    $[14] = setHoveredMarkerId;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const handleMouseEnter = t4;
  let t5;
  if ($[16] !== setHoveredMarkerId) {
    t5 = () => {
      isHoveredRef.current = false;
      applyHoverEffects(false);
      setHoveredMarkerId(null);
    };
    $[16] = setHoveredMarkerId;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const handleMouseLeave = t5;
  let t6;
  let t7;
  if ($[18] !== hoverKey || $[19] !== interactionStore) {
    t6 = () => {
      const unsubscribe = interactionStore.subscribe(state_0 => {
        const isGloballyHovered = state_0.hoveredMarkerId === hoverKey;
        if (isGloballyHovered !== isGloballyHoveredRef.current) {
          isGloballyHoveredRef.current = isGloballyHovered;
          applyHoverEffects(isHoveredRef.current || isGloballyHoveredRef.current);
        }
      });
      const state_1 = interactionStore.getState();
      const isGloballyHovered_0 = state_1.hoveredMarkerId === hoverKey;
      if (isGloballyHovered_0 !== isGloballyHoveredRef.current) {
        isGloballyHoveredRef.current = isGloballyHovered_0;
        applyHoverEffects(isHoveredRef.current || isGloballyHoveredRef.current);
      }
      return unsubscribe;
    };
    t7 = [interactionStore, hoverKey, applyHoverEffects];
    $[18] = hoverKey;
    $[19] = interactionStore;
    $[20] = t6;
    $[21] = t7;
  } else {
    t6 = $[20];
    t7 = $[21];
  }
  React.useEffect(t6, t7);
  const isHighlighted = !disableHighlight && isClicked;
  let t8;
  bb2: {
    if (data.relatedSups && data.relatedSups.length > 0) {
      t8 = data.relatedSups;
      break bb2;
    }
    let t9;
    if ($[22] !== data) {
      t9 = [data];
      $[22] = data;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t8 = t9;
  }
  const propertiesAtCoordinates = t8;
  const openPropertyOverlay = usePropertyInteractionStore(_temp3);
  let t9;
  if ($[24] !== coordinates || $[25] !== coordinatesString || $[26] !== data._id || $[27] !== data.projectId || $[28] !== data.projectName || $[29] !== disableOnClick || $[30] !== handleToggleClick || $[31] !== openPropertyOverlay || $[32] !== propertiesAtCoordinates) {
    t9 = e => {
      if (disableOnClick) {
        return;
      }
      e.stopPropagation();
      if (!coordinatesString || !coordinates) {
        return;
      }
      const initialSupId = data._id;
      openPropertyOverlay({
        sups: propertiesAtCoordinates,
        initialSupId,
        projectName: data.projectName || ""
      });
      handleToggleClick({
        coordinates,
        projectId: data.projectId || "",
        projectName: data.projectName || ""
      });
    };
    $[24] = coordinates;
    $[25] = coordinatesString;
    $[26] = data._id;
    $[27] = data.projectId;
    $[28] = data.projectName;
    $[29] = disableOnClick;
    $[30] = handleToggleClick;
    $[31] = openPropertyOverlay;
    $[32] = propertiesAtCoordinates;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const handleClick = t9;
  let t10;
  if ($[34] !== className) {
    t10 = cn("search-item-card", "flex w-full", "gap-0.5", "cursor-pointer", "transition-all duration-300", "min-h-[120px]", "bg-white", "rounded-2xl overflow-hidden", className);
    $[34] = className;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  const t11 = `${isHighlighted ? "shadow-xl" : ""}`;
  const t12 = rank <= LCP_PRIORITY_THRESHOLD;
  let t13;
  if ($[36] !== data || $[37] !== t11 || $[38] !== t12) {
    t13 = <SUPCard data={data} className={t11} shouldPrioritizeCoverImage={t12} />;
    $[36] = data;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== handleClick || $[41] !== handleMouseEnter || $[42] !== handleMouseLeave || $[43] !== t10 || $[44] !== t13) {
    t14 = <><div ref={elementRef} className={t10} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onClick={handleClick}>{t13}</div></>;
    $[40] = handleClick;
    $[41] = handleMouseEnter;
    $[42] = handleMouseLeave;
    $[43] = t10;
    $[44] = t13;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  return t14;
});
function _temp(s) {
  return s.trim();
}
function _temp2(state) {
  return state.setHoveredMarkerId;
}
function _temp3(state_2) {
  return state_2.openPropertyOverlay;
}